// === IMPORTS ===
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  SystemSnapshot,
  OfficeLocation,
  SimulationStats,
  TimeMachineIncident,
  TimeMachineFrame,
  SystemTimezone,
} from '../types';
import {
  buildCurrentSnapshot,
  validateSnapshotJSON,
  downloadSnapshotAsJSON,
  copySnapshotToClipboard,
  loadSavedSnapshots,
  saveSnapshotToLibrary,
  deleteSnapshotFromLibrary,
} from '../utils/snapshotHelper';
import {
  Camera,
  Download,
  Upload,
  Copy,
  Check,
  Trash2,
  RotateCcw,
  Code2,
  Search,
  AlertCircle,
  FileJson,
  X,
  Database,
  History,
  CheckCircle2,
  SlidersHorizontal,
} from 'lucide-react';
import '../styles/twin-drawers-shared.css';
import '../styles/twin-icons-shared.css';
import './SystemSnapshotDrawer.css';

interface SystemSnapshotDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  locations: OfficeLocation[];
  activeLocationId: string;
  mode: 'simulation' | 'editor';
  activeTimezoneIana: string;
  clockTime: string;
  simulationSpeed: number;
  isSimulationPlaying: boolean;
  simulationStats?: SimulationStats;
  incidents: TimeMachineIncident[];
  timeMachineFrames: TimeMachineFrame[];
  currentTimeMachineFrameIndex: number;
  isTimeMachineLive: boolean;
  systemTimezones: SystemTimezone[];
  userProfile?: any;
  onRestoreSnapshot: (snapshot: SystemSnapshot) => void;
}

type TabType = 'library' | 'inspector' | 'import';

// === COMPONENTE: GESTOR DE SNAPSHOTS Y ESTADO JSON DEL SISTEMA ===
export const SystemSnapshotDrawer: React.FC<SystemSnapshotDrawerProps> = ({
  isOpen,
  onClose,
  locations,
  activeLocationId,
  mode,
  activeTimezoneIana,
  clockTime,
  simulationSpeed,
  isSimulationPlaying,
  simulationStats,
  incidents,
  timeMachineFrames,
  currentTimeMachineFrameIndex,
  isTimeMachineLive,
  systemTimezones,
  userProfile,
  onRestoreSnapshot,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('library');
  const [savedSnapshots, setSavedSnapshots] = useState<SystemSnapshot[]>([]);

  const [newSnapshotName, setNewSnapshotName] = useState<string>('');
  const [newSnapshotDesc, setNewSnapshotDesc] = useState<string>('');
  const [newSnapshotTag, setNewSnapshotTag] = useState<'manual' | 'checkpoint' | 'incident'>('manual');
  const [justSavedId, setJustSavedId] = useState<string | null>(null);

  const [selectedSnapshotForInspect, setSelectedSnapshotForInspect] = useState<SystemSnapshot | null>(null);
  const [inspectSection, setInspectSection] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [isCopied, setIsCopied] = useState<boolean>(false);

  const [importJsonText, setImportJsonText] = useState<string>('');
  const [importError, setImportError] = useState<string | null>(null);
  const [previewImportSnapshot, setPreviewImportSnapshot] = useState<SystemSnapshot | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const drawerRef = useRef<HTMLElement>(null);

  const [restoringSnapshotId, setRestoringSnapshotId] = useState<string | null>(null);
  const [restoreSuccessMsg, setRestoreSuccessMsg] = useState<string | null>(null);

  // === CERRAR CON CLIC AFUERA O TECLA ESCAPE ===
  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (e: MouseEvent | TouchEvent | PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (drawerRef.current && drawerRef.current.contains(target)) return;
      if (target.closest('#confirm-delete-modal-overlay') || target.closest('#confirm-delete-modal-container')) return;
      onClose();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // === CARGAR SNAPSHOTS GUARDADOS AL ABRIR ===
  useEffect(() => {
    if (isOpen) {
      const list = loadSavedSnapshots();
      setSavedSnapshots(list);

      if (!selectedSnapshotForInspect) {
        const liveSnapshot = buildCurrentSnapshot({
          locations,
          activeLocationId,
          mode,
          activeTimezoneIana,
          clockTime,
          simulationSpeed,
          isSimulationPlaying,
          simulationStats,
          incidents,
          timeMachineFrames,
          currentTimeMachineFrameIndex,
          isTimeMachineLive,
          systemTimezones,
          userProfile,
          customName: 'Estado Actual en Vivo',
          tag: 'auto',
        });
        setSelectedSnapshotForInspect(liveSnapshot);
      }
    }
  }, [
    isOpen,
    locations,
    activeLocationId,
    mode,
    activeTimezoneIana,
    clockTime,
    simulationSpeed,
    isSimulationPlaying,
    simulationStats,
    incidents,
    timeMachineFrames,
    currentTimeMachineFrameIndex,
    isTimeMachineLive,
    systemTimezones,
    userProfile,
  ]);

  const handleCaptureSnapshot = () => {
    const newSnapshot = buildCurrentSnapshot({
      locations,
      activeLocationId,
      mode,
      activeTimezoneIana,
      clockTime,
      simulationSpeed,
      isSimulationPlaying,
      simulationStats,
      incidents,
      timeMachineFrames,
      currentTimeMachineFrameIndex,
      isTimeMachineLive,
      systemTimezones,
      userProfile,
      customName: newSnapshotName.trim() || undefined,
      customDescription: newSnapshotDesc.trim() || undefined,
      tag: newSnapshotTag,
    });

    const updatedList = saveSnapshotToLibrary(newSnapshot);
    setSavedSnapshots(updatedList);
    setSelectedSnapshotForInspect(newSnapshot);
    setJustSavedId(newSnapshot.metadata.id);
    setNewSnapshotName('');
    setNewSnapshotDesc('');

    setTimeout(() => setJustSavedId(null), 2500);
  };

  const handleConfirmRestore = (snapshot: SystemSnapshot) => {
    onRestoreSnapshot(snapshot);
    setRestoreSuccessMsg(`Snapshot "${snapshot.metadata.name}" successfully restored.`);
    setRestoringSnapshotId(null);
    setTimeout(() => setRestoreSuccessMsg(null), 3500);
  };

  const handleDeleteSnapshot = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = deleteSnapshotFromLibrary(id);
    setSavedSnapshots(updated);
    if (selectedSnapshotForInspect?.metadata.id === id) {
      setSelectedSnapshotForInspect(updated[0] || null);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      setImportJsonText(text);
      validateAndPreviewImport(text);
    };
    reader.readAsText(file);
  };

  const validateAndPreviewImport = (text: string) => {
    setImportError(null);
    setPreviewImportSnapshot(null);
    if (!text.trim()) return;

    const validation = validateSnapshotJSON(text);
    if (!validation.isValid || !validation.snapshot) {
      setImportError(validation.errors.join(' '));
    } else {
      setPreviewImportSnapshot(validation.snapshot);
    }
  };

  const handleApplyImportedSnapshot = () => {
    if (!previewImportSnapshot) return;

    const updatedList = saveSnapshotToLibrary(previewImportSnapshot);
    setSavedSnapshots(updatedList);

    onRestoreSnapshot(previewImportSnapshot);
    setRestoreSuccessMsg(`Snapshot importado "${previewImportSnapshot.metadata.name}" restaurado.`);
    setPreviewImportSnapshot(null);
    setImportJsonText('');
    setActiveTab('library');

    setTimeout(() => setRestoreSuccessMsg(null), 3500);
  };

  // === EXTRACCIÓN DE SECCIÓN JSON PARA EL INSPECTOR ===
  const filteredJsonContent = useMemo(() => {
    if (!selectedSnapshotForInspect) return '{}';

    let targetData: any = selectedSnapshotForInspect;

    if (inspectSection === 'locations') {
      targetData = selectedSnapshotForInspect.locations;
    } else if (inspectSection === 'agents') {
      targetData = selectedSnapshotForInspect.locations.reduce(
        (acc: any[], l) => acc.concat((l.agents || []).map((a) => ({ ...a, locationId: l.id, locationName: l.name }))),
        []
      );
    } else if (inspectSection === 'rooms') {
      targetData = selectedSnapshotForInspect.locations.reduce(
        (acc: any[], l) => acc.concat((l.rooms || []).map((r) => ({ ...r, locationId: l.id, locationName: l.name }))),
        []
      );
    } else if (inspectSection === 'items') {
      targetData = selectedSnapshotForInspect.locations.reduce(
        (acc: any[], l) => acc.concat((l.items || []).map((it) => ({ ...it, locationId: l.id, locationName: l.name }))),
        []
      );
    } else if (inspectSection === 'simulation') {
      targetData = selectedSnapshotForInspect.simulation;
    } else if (inspectSection === 'timeMachine') {
      targetData = {
        incidents: selectedSnapshotForInspect.simulation.incidents,
        framesCount: selectedSnapshotForInspect.simulation.timeMachineFramesCount,
        frames: selectedSnapshotForInspect.simulation.timeMachineFrames,
      };
    } else if (inspectSection === 'timezones') {
      targetData = selectedSnapshotForInspect.systemTimezones;
    } else if (inspectSection === 'summary') {
      targetData = selectedSnapshotForInspect.summary;
    } else if (inspectSection === 'metadata') {
      targetData = selectedSnapshotForInspect.metadata;
    }

    if (searchQuery.trim()) {
      try {
        const queryLower = searchQuery.toLowerCase();
        const jsonString = JSON.stringify(targetData, null, 2);
        const lines = jsonString.split('\n');
        const filteredLines = lines.filter((line) => line.toLowerCase().includes(queryLower));
        return filteredLines.length > 0
          ? `// Resultados para "${searchQuery}":\n` + filteredLines.join('\n')
          : `// No se encontraron coincidencias para "${searchQuery}".`;
      } catch {
        return JSON.stringify(targetData, null, 2);
      }
    }

    return JSON.stringify(targetData, null, 2);
  }, [selectedSnapshotForInspect, inspectSection, searchQuery]);

  const handleCopyJSON = async () => {
    const success = await copySnapshotToClipboard(
      searchQuery.trim() ? filteredJsonContent : selectedSnapshotForInspect
    );
    if (success) {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  // === RENDER ===
  return (
    <aside
      ref={drawerRef}
      id="system-snapshot-drawer"
      aria-label="Side Drawer: Snapshots & System State (JSON)"
      className={`twin-drawer twin-drawer--xl twin-snapshot-drawer ${isOpen ? 'twin-drawer--open' : 'twin-drawer--closed'}`}
    >
      {/* === HEADER === */}
      <div className="twin-drawer-header">
        <div className="twin-drawer-header-left">
          <div className="twin-snapshot-icon-badge">
            <Camera className="twin-icon-sm" />
          </div>
          <div>
            <div className="twin-snapshot-title-row">
              <h2 className="twin-drawer-title">Snapshots & JSON State</h2>
              <span className="twin-snapshot-version-tag">v1.0</span>
            </div>
            <p className="twin-snapshot-subtitle">Capture, inspect and restore full system state</p>
          </div>
        </div>
        <button type="button" onClick={onClose} className="twin-drawer-close-btn" aria-label="Close snapshots panel" title="Close panel (Esc)">
          <X className="twin-icon-sm" />
        </button>
      </div>

      {/* === NOTIFICACIÓN GLOBAL DE ÉXITO === */}
      {restoreSuccessMsg && (
        <div className="twin-snapshot-success-bar">
          <div className="twin-snapshot-success-left">
            <CheckCircle2 className="twin-icon-sm" />
            <span>{restoreSuccessMsg}</span>
          </div>
          <button onClick={() => setRestoreSuccessMsg(null)} className="twin-snapshot-success-close">
            <X className="twin-icon-xs" />
          </button>
        </div>
      )}

      {/* === PESTAÑAS === */}
      <div className="twin-snapshot-tabs">
        <button type="button" onClick={() => setActiveTab('library')} className={`twin-snapshot-tab ${activeTab === 'library' ? 'twin-snapshot-tab--active' : ''}`}>
          <History className="twin-icon-sm" />
          <span>History & Capture ({savedSnapshots.length})</span>
        </button>
        <button type="button" onClick={() => setActiveTab('inspector')} className={`twin-snapshot-tab ${activeTab === 'inspector' ? 'twin-snapshot-tab--active' : ''}`}>
          <Code2 className="twin-icon-sm" />
          <span>Analyzer & JSON Extractor</span>
        </button>
        <button type="button" onClick={() => setActiveTab('import')} className={`twin-snapshot-tab ${activeTab === 'import' ? 'twin-snapshot-tab--active' : ''}`}>
          <Upload className="twin-icon-sm" />
          <span>Load / Import Snapshot</span>
        </button>
      </div>

      {/* === CUERPO === */}
      <div className="twin-drawer-body">
        {/* === TAB 1: LIBRARY & CAPTURE === */}
        {activeTab === 'library' && (
          <div className="twin-section-gap-lg">
            <div className="twin-snapshot-capture-card">
              <div className="twin-row-between">
                <div className="twin-snapshot-capture-title">
                  <Camera className="twin-icon-sm" />
                  <h3>Capture Current System Snapshot</h3>
                </div>
                <span className="twin-snapshot-clock-badge">{clockTime} • {activeTimezoneIana}</span>
              </div>

              <div className="twin-grid-2">
                <div>
                  <label className="twin-field-label">Name or Label (Optional)</label>
                  <input
                    type="text"
                    placeholder={`Snapshot ${locations.find((l) => l.id === activeLocationId)?.name || 'Floor'} (${clockTime})`}
                    value={newSnapshotName}
                    onChange={(e) => setNewSnapshotName(e.target.value)}
                    className="twin-input twin-input--snapshot"
                  />
                </div>
                <div>
                  <label className="twin-field-label">Category / Type</label>
                  <select value={newSnapshotTag} onChange={(e) => setNewSnapshotTag(e.target.value as any)} className="twin-select twin-input--snapshot">
                    <option value="manual">Manual (Checkpoint)</option>
                    <option value="checkpoint">Simulation Milestone</option>
                    <option value="incident">Incident Diagnostics</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="twin-field-label">Description / Analysis Notes (Optional)</label>
                <input
                  type="text"
                  placeholder="e.g. Test with 12 agents in boardroom and bottleneck at cafeteria..."
                  value={newSnapshotDesc}
                  onChange={(e) => setNewSnapshotDesc(e.target.value)}
                  className="twin-input twin-input--snapshot"
                />
              </div>

              <div className="twin-row-between twin-snapshot-capture-footer">
                <div className="twin-snapshot-capture-hint">
                  <Database className="twin-icon-sm" />
                  <span>
                    Includes {locations.length} floors, {locations.reduce((acc, l) => acc + (l.agents?.length || 0), 0)} agents and full telemetry
                  </span>
                </div>
                <button type="button" onClick={handleCaptureSnapshot} className="twin-btn twin-btn--cyan-primary">
                  <Camera className="twin-icon-sm" />
                  <span>Capture Snapshot Now</span>
                </button>
              </div>
            </div>

            <div className="twin-section-gap">
              <div className="twin-row-between twin-snapshot-list-header">
                <span>Saved Snapshots ({savedSnapshots.length})</span>
                <span className="twin-snapshot-list-hint">Click "Restore" to revert to saved state</span>
              </div>

              {savedSnapshots.length === 0 ? (
                <div className="twin-snapshot-empty-state">
                  <Camera className="twin-icon-lg" />
                  <p className="twin-snapshot-empty-title">No snapshots stored in history</p>
                  <p className="twin-snapshot-empty-desc">Capture the current simulation state or import a JSON file to easily restore it at any time.</p>
                </div>
              ) : (
                savedSnapshots.map((snap) => {
                  const isJustSaved = justSavedId === snap.metadata.id;
                  const isConfirming = restoringSnapshotId === snap.metadata.id;

                  return (
                    <div key={snap.metadata.id} className={`twin-snapshot-item ${isJustSaved ? 'twin-snapshot-item--saved' : ''}`}>
                      <div className="twin-row-between twin-snapshot-item-top">
                        <div>
                          <div className="twin-snapshot-item-title-row">
                            <h4 className="twin-snapshot-item-name">{snap.metadata.name}</h4>
                            <span className={`twin-snapshot-tag-pill twin-snapshot-tag-pill--${snap.metadata.tag || 'manual'}`}>
                              {snap.metadata.tag || 'manual'}
                            </span>
                            <span className="twin-snapshot-item-date">{new Date(snap.metadata.createdAt).toLocaleString()}</span>
                          </div>
                          {snap.metadata.description && <p className="twin-snapshot-item-desc">{snap.metadata.description}</p>}
                        </div>

                        <div className="twin-snapshot-item-actions">
                          <button type="button" onClick={() => { setSelectedSnapshotForInspect(snap); setActiveTab('inspector'); }} className="twin-snapshot-action-btn" title="Inspect JSON structure">
                            <Code2 className="twin-icon-sm" />
                            <span>JSON</span>
                          </button>
                          <button type="button" onClick={() => downloadSnapshotAsJSON(snap)} className="twin-snapshot-icon-only-btn" title="Download .json file">
                            <Download className="twin-icon-sm" />
                          </button>
                          <button type="button" onClick={(e) => handleDeleteSnapshot(snap.metadata.id, e)} className="twin-snapshot-icon-only-btn twin-snapshot-icon-only-btn--danger" title="Delete snapshot">
                            <Trash2 className="twin-icon-sm" />
                          </button>
                        </div>
                      </div>

                      <div className="twin-snapshot-chips-row">
                        <span className="twin-snapshot-chip">🕒 {snap.metadata.clockTime}</span>
                        <span className="twin-snapshot-chip">🏢 {snap.summary.totalLocations} floors</span>
                        <span className="twin-snapshot-chip">🤖 {snap.summary.totalAgents} agents ({snap.summary.activeAgentsCount} active)</span>
                        <span className="twin-snapshot-chip">🚪 {snap.summary.totalRooms} rooms</span>
                        <span className="twin-snapshot-chip twin-snapshot-chip--emerald">⚡ {snap.summary.avgEfficiency}% efficiency</span>
                      </div>

                      {isConfirming ? (
                        <div className="twin-snapshot-confirm-box">
                          <div className="twin-snapshot-confirm-left">
                            <AlertCircle className="twin-icon-sm" />
                            <span>Restore entire system to this snapshot state?</span>
                          </div>
                          <div className="twin-snapshot-confirm-actions">
                            <button type="button" onClick={() => setRestoringSnapshotId(null)} className="twin-link-btn">Cancel</button>
                            <button type="button" onClick={() => handleConfirmRestore(snap)} className="twin-btn twin-btn--amber-primary twin-btn--sm">
                              <RotateCcw className="twin-icon-xs" />
                              <span>Confirm Restore</span>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="twin-snapshot-restore-row">
                          <button type="button" onClick={() => setRestoringSnapshotId(snap.metadata.id)} className="twin-snapshot-restore-btn">
                            <RotateCcw className="twin-icon-sm" />
                            <span>Restore this Snapshot</span>
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* === TAB 2: JSON INSPECTOR & EXTRACTOR === */}
        {activeTab === 'inspector' && (
          <div className="twin-section-gap">
            <div className="twin-snapshot-inspect-header">
              <div className="twin-snapshot-inspect-select-row">
                <FileJson className="twin-icon-sm twin-color-cyan" />
                <span className="twin-snapshot-inspect-label">Inspecting:</span>
                <select
                  value={selectedSnapshotForInspect?.metadata.id || ''}
                  onChange={(e) => { const snap = savedSnapshots.find((s) => s.metadata.id === e.target.value); if (snap) setSelectedSnapshotForInspect(snap); }}
                  className="twin-select twin-snapshot-inspect-select"
                >
                  {savedSnapshots.map((s) => (
                    <option key={s.metadata.id} value={s.metadata.id}>{s.metadata.name} ({s.metadata.clockTime})</option>
                  ))}
                  {!savedSnapshots.some((s) => s.metadata.id === selectedSnapshotForInspect?.metadata.id) && (
                    <option value={selectedSnapshotForInspect?.metadata.id}>{selectedSnapshotForInspect?.metadata.name || 'Current Live State'}</option>
                  )}
                </select>
              </div>

              <div className="twin-snapshot-inspect-actions">
                <button type="button" onClick={handleCopyJSON} className="twin-snapshot-action-btn">
                  {isCopied ? (
                    <>
                      <Check className="twin-icon-sm twin-stat-positive" />
                      <span className="twin-stat-positive">Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="twin-icon-sm twin-color-cyan" />
                      <span>Copy JSON</span>
                    </>
                  )}
                </button>
                {selectedSnapshotForInspect && (
                  <button type="button" onClick={() => downloadSnapshotAsJSON(selectedSnapshotForInspect)} className="twin-btn twin-btn--cyan-primary twin-btn--sm">
                    <Download className="twin-icon-sm" />
                    <span>Download JSON</span>
                  </button>
                )}
              </div>
            </div>

            <div className="twin-section-gap-sm">
              <label className="twin-field-label twin-inline-icon-label">
                <SlidersHorizontal className="twin-icon-sm twin-color-cyan" />
                <span>Extract Section from JSON Structure:</span>
              </label>
              <div className="twin-snapshot-section-chips">
                {[
                  { id: 'all', label: 'Complete JSON (All)' },
                  { id: 'locations', label: 'Locations & Floors' },
                  { id: 'agents', label: 'AI Agents' },
                  { id: 'rooms', label: 'Rooms & Zones' },
                  { id: 'items', label: 'Furniture & Items' },
                  { id: 'simulation', label: 'Simulation & Stats' },
                  { id: 'timeMachine', label: 'Time Machine' },
                  { id: 'timezones', label: 'Timezones' },
                  { id: 'summary', label: 'Metric Summary' },
                ].map((sec) => (
                  <button key={sec.id} type="button" onClick={() => { setInspectSection(sec.id); setSearchQuery(''); }} className={`twin-snapshot-section-chip ${inspectSection === sec.id ? 'twin-snapshot-section-chip--active' : ''}`}>
                    {sec.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="twin-snapshot-search-wrap">
              <Search className="twin-icon-sm twin-snapshot-search-icon" />
              <input type="text" placeholder="Search any key or value in the JSON payload..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="twin-input twin-snapshot-search-input" />
            </div>

            {selectedSnapshotForInspect && (
              <div className="twin-snapshot-stats-grid">
                <div className="twin-snapshot-stat-box"><span className="twin-snapshot-stat-label">Floor / Location</span><p className="twin-snapshot-stat-value">{selectedSnapshotForInspect.metadata.activeLocationName || 'Main Floor'}</p></div>
                <div className="twin-snapshot-stat-box"><span className="twin-snapshot-stat-label">Total Agents</span><p className="twin-snapshot-stat-value twin-color-cyan">{selectedSnapshotForInspect.summary.totalAgents} ({selectedSnapshotForInspect.summary.activeAgentsCount} active)</p></div>
                <div className="twin-snapshot-stat-box"><span className="twin-snapshot-stat-label">Efficiency & SLA</span><p className="twin-snapshot-stat-value twin-stat-positive">{selectedSnapshotForInspect.summary.avgEfficiency}% • {selectedSnapshotForInspect.summary.throughputTasksPerMin} t/min</p></div>
                <div className="twin-snapshot-stat-box"><span className="twin-snapshot-stat-label">System Time</span><p className="twin-snapshot-stat-value">{selectedSnapshotForInspect.metadata.clockTime} ({selectedSnapshotForInspect.metadata.activeTimezoneIana.split('/').pop()})</p></div>
              </div>
            )}

            <div className="twin-snapshot-json-viewer">
              <div className="twin-snapshot-json-viewer-header">
                <span>snapshot_{selectedSnapshotForInspect?.metadata.id || 'live'}.json ({inspectSection})</span>
                <span>{filteredJsonContent.length.toLocaleString()} characters</span>
              </div>
              <pre className="twin-snapshot-json-code">{filteredJsonContent}</pre>
            </div>
          </div>
        )}

        {/* === TAB 3: IMPORT SNAPSHOT === */}
        {activeTab === 'import' && (
          <div className="twin-section-gap-lg">
            <div className="twin-snapshot-import-card">
              <div className="twin-snapshot-import-title">
                <Upload className="twin-icon-sm twin-color-cyan" />
                <h3>Load External JSON Snapshot</h3>
              </div>
              <p className="twin-snapshot-import-desc">
                Upload a previously exported <code className="twin-color-cyan">.json</code> file or paste the JSON structure to analyze and restore the system state.
              </p>

              <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept=".json,application/json" className="twin-hidden" />

              <div onClick={() => fileInputRef.current?.click()} className="twin-snapshot-dropzone">
                <FileJson className="twin-icon-lg twin-snapshot-dropzone-icon" />
                <p className="twin-snapshot-dropzone-title">Click to select JSON file or drag and drop here</p>
                <p className="twin-snapshot-dropzone-hint">Compatible with SystemSnapshot v1.0.0 format</p>
              </div>

              <div>
                <label className="twin-field-label">Or paste raw JSON content here:</label>
                <textarea
                  rows={5}
                  value={importJsonText}
                  onChange={(e) => { setImportJsonText(e.target.value); validateAndPreviewImport(e.target.value); }}
                  placeholder='{"metadata": { ... }, "locations": [ ... ]}'
                  className="twin-textarea twin-snapshot-json-input"
                />
              </div>

              {importError && (
                <div className="twin-snapshot-error-box">
                  <AlertCircle className="twin-icon-sm" />
                  <div>
                    <p className="twin-snapshot-error-title">Error validating JSON snapshot:</p>
                    <p className="twin-snapshot-error-detail">{importError}</p>
                  </div>
                </div>
              )}

              {previewImportSnapshot && (
                <div className="twin-snapshot-preview-box">
                  <div className="twin-row-between">
                    <div className="twin-snapshot-preview-title">
                      <CheckCircle2 className="twin-icon-sm" />
                      <span>Valid Snapshot Ready to Restore</span>
                    </div>
                    <span className="twin-snapshot-preview-time">{previewImportSnapshot.metadata.clockTime}</span>
                  </div>

                  <div className="twin-snapshot-preview-grid">
                    <div className="twin-snapshot-preview-item"><span className="twin-snapshot-preview-item-label">Name</span><span className="twin-snapshot-preview-item-value">{previewImportSnapshot.metadata.name}</span></div>
                    <div className="twin-snapshot-preview-item"><span className="twin-snapshot-preview-item-label">Floors</span><span className="twin-snapshot-preview-item-value twin-color-cyan">{previewImportSnapshot.locations.length} locations</span></div>
                    <div className="twin-snapshot-preview-item"><span className="twin-snapshot-preview-item-label">Agents</span><span className="twin-snapshot-preview-item-value">{previewImportSnapshot.summary.totalAgents} agents</span></div>
                    <div className="twin-snapshot-preview-item"><span className="twin-snapshot-preview-item-label">Efficiency</span><span className="twin-snapshot-preview-item-value twin-stat-positive">{previewImportSnapshot.summary.avgEfficiency}%</span></div>
                  </div>

                  <button type="button" onClick={handleApplyImportedSnapshot} className="twin-snapshot-apply-btn">
                    <RotateCcw className="twin-icon-sm" />
                    <span>Restore Snapshot and Apply to System</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* === FOOTER === */}
      <div className="twin-drawer-footer">
        <span className="twin-snapshot-footer-format">
          <Database className="twin-icon-sm twin-color-cyan" />
          <span>Universal JSON Format (SystemSnapshot v1.0.0)</span>
        </span>
        <button type="button" onClick={onClose} className="twin-btn twin-btn--secondary">Close</button>
      </div>
    </aside>
  );
};